import { useCallback, useRef } from 'react'
import useSWR from 'swr'
import { useApiRestClient } from '../providers/api-rest-client/api-rest-client'
import { composeSwrKey } from '../stale-while-revalidate/cache-key'
import { formatResponseError } from '../utils/format-response-error'

export const noop = () => {}

export const fetcherGenerator =
	(apiFunction, setCancel, client) =>
	([
		,
		// key,
		args,
		// store,
		// currency,
		// userGroup,
	]) => {
		// receives also the default header values
		// eslint-disable-next-line import/no-named-as-default-member
		const controller = new AbortController()

		if (typeof setCancel === 'function') {
			setCancel(controller.abort)
		}

		return apiFunction({
			controller,
			client,
			...args,
		})
			.then((res) => res.data)
			.catch((err) => {
				const error = formatResponseError(err)
				error.__CANCEL__ = err.__CANCEL__
				throw error
			})
	}

/**
 * @param {function} method
 * @param {string|array|function|null|undefined} [key]
 * @param {Object} args - must be serializable otherwise data will not be passed to the method.
 * @param {boolean} paused - force paused.
 * @param {Object} options - useSWR options @see https://swr.vercel.app/docs/options#options.
 * @returns {Object}
 */
export const useApi = ({ method, key, args = {}, paused = false, options = {} }) => {
	const client = useApiRestClient()

	const cancelRef = useRef(noop)
	const setCancel = useCallback((cancel) => (cancelRef.current = cancel), [])

	const swrKey = composeSwrKey({ client, method, key, args })

	const { data, error, mutate, isValidating, ...swr } = useSWR(swrKey, fetcherGenerator(method, setCancel, client), {
		fetcherGenerator,
		bhLocalPaused: paused,
		...options,
		use: options.use ?? [], // send always an array so swr can merge with the default middleware
	})

	return {
		...swr,
		data,
		error,
		mutate,
		isValidating: isValidating,
		cancel: cancelRef.current,
		cancelled: error?.__CANCEL__,
	}
}
